import React, { useEffect, useRef, useState } from "react";
import CartButton from "./CartButton";
import { ChevronLeft, ChevronRight, PlayCircle, Sparkles } from "lucide-react";
import CommonButton from "../CommonButton";
import { ProductItem } from "../../utils/Customer/CustomerTypes";

interface Props {
    items: ProductItem[];
    handleViewAll?: () => void;
    showViewAll?: boolean;
    layout?: "overlay" | "below";
    title?: React.ReactNode;
    description?: string;
    badgeText?: string;
    showallDetails?: boolean
}

const VideoGridSection: React.FC<Props> = ({ items, handleViewAll, showViewAll = true, layout = "overlay", title,
    description, badgeText = 'Trending', showallDetails = true }) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
    const [cartQty, setCartQty] = useState<{ [key: number]: number }>({});
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(true);

    const handleAdd = (index: number) => setCartQty((prev) => ({ ...prev, [index]: 1 }));

    const handleIncrement = (index: number) => {
        setCartQty((prev) => {
            const current = prev[index] || 0;
            return current >= 10 ? prev : { ...prev, [index]: current + 1 };
        });
    };

    const handleDecrement = (index: number) => {
        setCartQty((prev) => {
            const current = prev[index];
            if (current === 1) {
                const updated = { ...prev };
                delete updated[index];
                return updated;
            }
            return { ...prev, [index]: current - 1 };
        });
    };

    const pauseAllVideos = () => {
        videoRefs.current.forEach((v) => v?.pause());
    };

    const playVideo = async (video?: HTMLVideoElement | null) => {
        if (!video) return;
        try { await video.play(); } catch { }
    };

    const scroll = (direction: "left" | "right") => {
        if (scrollRef.current) {
            scrollRef.current.scrollBy({
                left: direction === "left" ? -320 : 320,
                behavior: "smooth",
            });
        }
    };


    return (
        <div className="py-5">
            {/* CAROUSEL CONTAINER */}
            <div className="w-full relative">
                <div
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 pt-2"
                >
                    {items.map((item, index) => {
                        const qty = cartQty[index] || 0;

                        return (
                            <div
                                key={index}
                                className="group w-[240px] md:w-[280px] flex flex-col flex-shrink-0 snap-start bg-white rounded-3xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-500 hover:-translate-y-2"
                                onMouseEnter={() => {
                                    pauseAllVideos();
                                    playVideo(videoRefs.current[index]);
                                }}
                                onMouseLeave={pauseAllVideos}
                            >
                                {/* VIDEO WRAPPER */}
                                <div className="relative h-[380px] md:h-[420px] overflow-hidden">
                                    <video
                                        ref={(el) => {
                                            videoRefs.current[index] = el;
                                        }}
                                        src={item.video[0]?.image}
                                        loop
                                        muted
                                        playsInline
                                        className="w-full h-full object-cover"
                                    />

                                    {/* PLAY OVERLAY (shown when not playing) */}
                                    <div className="absolute inset-0 flex items-center justify-center bg-black/10 group-hover:bg-transparent transition-colors">
                                        <PlayCircle className="text-white/70 w-8 h-8 group-hover:opacity-0 transition-opacity" />
                                    </div>

                                    {/* TRENDING BADGE */}
                                    <div className="absolute top-4 left-4 animate-hurry">
                                        <div className="bg-offerColor text-logoHeading text-xs font-black px-2 py-1 rounded-lg flex items-center gap-1 shadow-sm">
                                            <Sparkles size={10} /> {badgeText}
                                        </div>
                                    </div>

                                    {layout === "overlay" && (
                                        <div className="absolute bottom-3 left-3 right-3">
                                            <div className="mt-3">
                                                {showallDetails ? (
                                                    <div className="flex items-center gap-3 backdrop-blur-md bg-white/80 p-3 rounded-2xl border border-white/50 shadow-lg">

                                                        <img
                                                            src={item.productimage}
                                                            alt={item.productname}
                                                            className="w-12 h-12 rounded-xl object-cover ring-2 ring-mainBrandColor/20"
                                                        />
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm font-bold text-gray-900 truncate">
                                                                {item.productname}
                                                            </p>
                                                            <p className="text-mediumGreen font-bold text-sm">
                                                                {item.price}
                                                            </p>
                                                        </div>

                                                        <div className="mt-3">
                                                            <CartButton
                                                                qty={qty}
                                                                onAdd={() => handleAdd(index)}
                                                                onIncrement={() => handleIncrement(index)}
                                                                onDecrement={() => handleDecrement(index)}
                                                            />
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <CommonButton
                                                        text="Book Now"
                                                        variant="filled"
                                                        rounded="md"
                                                        className="bg-mainBrandColor"
                                                        textContainerClassName="py-2 px-2"
                                                    />
                                                )}

                                            </div>
                                        </div>
                                    )}
                                </div>

                                {
                                    layout === "below" && (
                                        <div className="p-4 flex flex-col gap-3">
                                            <div className="flex items-start justify-between gap-2">
                                                <div>
                                                    <h3 className="font-bold text-gray-800 text-base leading-tight truncate w-32">
                                                        {item.productname}
                                                    </h3>
                                                    <p className="text-logoHeading font-black mt-1">{item.price}</p>
                                                </div>
                                                <img
                                                    src={item.productimage}
                                                    className="w-12 h-12 rounded-lg object-cover border border-mediumgray"
                                                    alt="thumb"
                                                />
                                            </div>
                                            <CartButton
                                                qty={qty}
                                                onAdd={() => handleAdd(index)}
                                                onIncrement={() => handleIncrement(index)}
                                                onDecrement={() => handleDecrement(index)}
                                            />
                                        </div>
                                    )
                                }
                            </div>
                        );
                    })}
                </div>
            </div>
        </div >
    );
};

export default VideoGridSection;